import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useSyncStatus } from "$/api/bridge";
import { Button } from "$/lib/components";
import Modal from "$/lib/components/atoms/Modal/Modal";
import { displayMessage } from "$/lib/components/molecules/CustomToast/CustomToast.utils";
import SynchronizeIcon from "$/lib/icons/SynchronizeIcon";
import { useBridgeSyncActive } from "$/routes/_user-layout/-features/hooks/useBridgeSyncActive";

// Backstop: never keep the modal / polling alive longer than this.
const MAX_SYNC_DURATION_MS = 5 * 60 * 1000;
// Delay before the modal actually appears, to avoid a flash on a fast sync.
const MODAL_APPEAR_DELAY_MS = 2 * 1000;

/**
 * Renders the "Synchronisation en cours" modal and drives the post-connection
 * bank sync: polls `sync-status`, informs the user on completion/failure, and
 * refetches app data. Mounted once in the user layout so it persists across
 * route changes.
 */
export default function BankSyncTracker() {
  const queryClient = useQueryClient();
  const [syncActive, setSyncActive] = useBridgeSyncActive();
  const [modalVisible, setModalVisible] = useState(false);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // Poll the backend only while a sync is active.
  const { data: syncStatus } = useSyncStatus(syncActive);

  const stopTracking = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setSyncActive(false);
    setModalVisible(false);
  };

  // React to the polled status. Key off the explicit status value so an
  // "unknown"/null tick (no info yet) is never mistaken for success or failure.
  useEffect(() => {
    if (!syncActive || !syncStatus) return;

    if (syncStatus.status === "done") {
      queryClient.refetchQueries();
      displayMessage("Synchronisation terminée avec succès !", "success");
      stopTracking();
    } else if (syncStatus.status === "failed") {
      queryClient.refetchQueries();
      displayMessage(
        "La synchronisation a échoué. Veuillez réessayer.",
        "error",
      );
      stopTracking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncActive, syncStatus, queryClient]);

  // Show the modal (after a short delay) and set up the safety-net refetches /
  // auto-close whenever a sync becomes active.
  useEffect(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    if (!syncActive) {
      setModalVisible(false);
      return;
    }

    const appearTimeout = setTimeout(
      () => setModalVisible(true),
      MODAL_APPEAR_DELAY_MS,
    );

    // Bridge transactions can trickle in after the item is marked "done", so we
    // keep pulling fresh data a few times while the sync window is open.
    const refetchAppData = () =>
      queryClient.refetchQueries({
        predicate: (query) => !query.queryKey.includes("syncStatus"),
      });
    const refetch1 = setTimeout(refetchAppData, 5 * 1000);
    const refetch2 = setTimeout(refetchAppData, 15 * 1000);
    const refetch3 = setTimeout(refetchAppData, 60 * 1000);
    const refetch4 = setTimeout(refetchAppData, 3 * 60 * 1000);

    // Hard backstop: if the sync never reports a terminal state, stop anyway.
    const autoClose = setTimeout(stopTracking, MAX_SYNC_DURATION_MS);

    timeoutsRef.current = [
      appearTimeout,
      refetch1,
      refetch2,
      refetch3,
      refetch4,
      autoClose,
    ];

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncActive, queryClient]);

  return (
    <SyncingDataModal isOpen={modalVisible} onClose={() => setModalVisible(false)} />
  );
}

type SyncingDataModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

function SyncingDataModal({ isOpen, onClose }: SyncingDataModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} backdropClassName="bg-primary/10">
      <div className="flex w-[460px] max-w-[80vw] flex-col items-center text-center">
        <div className="flex flex-col items-center">
          <div className="mx-auto w-fit rounded-full">
            <SynchronizeIcon className="mb-4 h-[64px] w-[64px]" />
          </div>
          <h2 className="w-full items-center pb-2 text-base font-bold">
            Synchronisation en cours
          </h2>
        </div>
        <div className="flex flex-col items-center gap-2">
          <p className="w-full pt-2 text-left text-sm font-semibold text-textColor">
            Actualisez la page d'ici quelques minutes le temps que vos
            transactions bancaires se synchronisent avec Libe.
          </p>
          <p className="w-full pb-2 text-left text-xs font-medium text-textInfo">
            À noter les transactions les plus lointaines peuvent arriver jusqu'à
            demain.
          </p>
        </div>
        <div className="mt-4 flex justify-center gap-3">
          <Button
            className="!h-[36px] bg-[#0E0549] !px-8 text-sm font-medium text-white hover:bg-primary/90"
            onClick={onClose}
          >
            C'est compris !
          </Button>
        </div>
      </div>
    </Modal>
  );
}
